import { useMemo } from 'react';
import { useOrganization } from '@context/OrganizationContext';
import useOrganizationStructure from '@context/OrganizationContext/useOrganizationStructure';
import { useVendor } from '@context/VendorContext';

import config from '../config';
import type { OrganizationProfile } from '../types/organizationProfile';

const useVendorHasFeatureAccess = (featureNameId: string) => {
  const { vendor, isFetched } = useVendor();
  return useMemo(
    () => isFetched && (vendor.features?.includes(featureNameId) || false),
    [featureNameId, vendor.features, isFetched]
  );
};

export const useVendorFeatureAccess = () => {
  return {
    hasEmbeddableSchedule: useVendorHasFeatureAccess(
      config.features.embeddableSchedules
    ),
    hasEmbeddableTerritories: useVendorHasFeatureAccess(
      config.features.embeddableTerritories
    ),
    hasMasterCatalog: useVendorHasFeatureAccess(config.features.masterCatalog),
    hasTerritories: useVendorHasFeatureAccess(config.features.territories),
  };
};

export const useOrganizationHasFeatureAccess = (
  featureNameId: string,
  fallbackFeatureNameId?: string
) => {
  const { organization, isFetched } = useOrganization();
  const result = useOrganizationHasFeatureAccessForOrganization(
    featureNameId,
    organization,
    fallbackFeatureNameId
  );
  return isFetched && result;
};

export const useOrganizationHasFeatureAccessForOrganization = (
  featureNameId: string,
  organization: OrganizationProfile,
  fallbackFeatureNameId?: string
) => {
  const features = useMemo(
    () => [
      ...(organization.featuresByEntity?.allFeatureSkus ||
        organization.features ||
        []),
    ],
    [organization.featuresByEntity, organization.features]
  );

  return useMemo(
    () =>
      features.includes(featureNameId) ||
      (fallbackFeatureNameId
        ? features.includes(fallbackFeatureNameId)
        : false),
    [featureNameId, fallbackFeatureNameId, features]
  );
};

export const useOrganizationFeatureLimits = () => {
  const personnelLimits = usePersonnelLimits();
  const vendorLimits = useVendorLimits();
  const organizationLimits = useOrganizationLimits();
  return useMemo(
    () => ({
      personnelLimits,
      vendorLimits,
      organizationLimits,
    }),
    [organizationLimits, personnelLimits, vendorLimits]
  );
};

export const useOrganizationFeatureAccess = () => {
  const hasEmbeddableSchedule = useOrganizationHasFeatureAccess(
    config.features.embeddableSchedules
  );

  const hasEmbeddableTerritories = useOrganizationHasFeatureAccess(
    config.features.embeddableTerritories
  );

  const hasMasterCatalogAccess = useOrganizationHasFeatureAccess(
    config.features.masterCatalog
  );

  const hasTerritories = useOrganizationHasFeatureAccess(
    config.features.territories
  );

  const hasQuotesAccess = useOrganizationHasFeatureAccess(
    config.features.quotes
  );

  const hasInvoiceTipping = useOrganizationHasFeatureAccess(
    config.features.invoiceTipping
  );

  const hasInvoiceEditingAccess = useOrganizationHasFeatureAccess(
    config.features.invoiceEditing
  );

  return useMemo(
    () => ({
      hasEmbeddableSchedule,
      hasEmbeddableTerritories,
      hasMasterCatalogAccess,
      hasTerritories,
      hasQuotesAccess,
      hasInvoiceTipping,
      hasInvoiceEditingAccess,
    }),
    [
      hasEmbeddableSchedule,
      hasEmbeddableTerritories,
      hasInvoiceEditingAccess,
      hasInvoiceTipping,
      hasMasterCatalogAccess,
      hasQuotesAccess,
      hasTerritories,
    ]
  );
};
//limits
export const useVendorLimits = () => {
  const { organizationId, isFetched: organizationIsFetched } =
    useOrganization();
  const { isFetched: structureIsFetched } =
    useOrganizationStructure(organizationId);
  const hasOrganizationsAccess = useOrganizationHasFeatureAccess(
    config.features.organizations
  );

  return {
    canAddVendor: hasOrganizationsAccess, // TODO: Pull this from org feature seats,
    isFetched: organizationIsFetched && structureIsFetched,
  };
};

const useOrganizationLimits = () => {
  const hasOrganizationsAccess = useOrganizationHasFeatureAccess(
    config.features.organizations
  );

  return {
    canAddOrganization: hasOrganizationsAccess,
  };
};

export const usePersonnelLimits = () => {
  const hasOrganizationsAccess = useOrganizationHasFeatureAccess(
    config.features.organizations
  );

  return {
    canAddPersonnel: hasOrganizationsAccess, // TODO: Pull this from org feature personnel seats,
  };
};
